import React, { useEffect, useState } from "react";
import { Card, Table, Space, Input, Tag, Modal, InputNumber, Form, Button, Select, message } from "antd";
import { Dropdown } from "antd";
import { MoreOutlined, EyeOutlined, SearchOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { axiosInstance, BASE_API_CONFIG } from "../../util/Api";
import { fetchSuccess, fetchStart } from "../../appRedux/actions";

const { TextArea } = Input;
const { Option } = Select;

const FreightBookingsList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [bookingsData, setBookingsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [form] = Form.useForm();

  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    status: '',
    search: ''
  });

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      confirmed: 'green',
      in_transit: 'blue',
      delivered: 'purple',
      cancelled: 'red',
    };
    return colors[status] || 'default';
  };

  const columns = [
    {
      title: "Booking ID",
      key: "id",
      dataIndex: "id",
      render: (id) => <div className={"highlighted-column"}>{id}</div>,
    },
    {
      title: "Company",
      key: "company",
      render: (record) => <div>{record?.user?.companyName || 'N/A'}</div>,
    },
    {
      title: "Product",
      key: "product",
      dataIndex: "productName",
      render: (text) => <div>{text || 'N/A'}</div>,
    },
    {
      title: "Container Type",
      key: "containerType",
      dataIndex: "containerType",
      render: (text) => text ? <Tag color="blue">{text}</Tag> : 'N/A',
    },
    {
      title: "Route",
      key: "route",
      render: (record) => (
        <div>{record.loadingPort || 'N/A'} → {record.destinationPort || 'N/A'}</div>
      ),
    },
    {
      title: "Cargo Readiness",
      key: "cargoReadinessDate",
      dataIndex: "cargoReadinessDate",
      render: (text) => text ? new Date(text).toLocaleDateString() : 'N/A',
    },
{
  title: "Freight Rate",
  key: "freightRate",
  dataIndex: "freightRate",
  render: (freightRate, record) => 
    freightRate && record?.currency ? `${record.currency} ${parseFloat(freightRate)}` : '-',
},

    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status ? status.replace('_', ' ').toUpperCase() : 'PENDING'}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <div className="myexim-action-icon" onClick={(e) => e.stopPropagation()}>
          <Dropdown
            menu={{
              items: [
                {
                  label: "View Details",
                  key: "1",
                  icon: <EyeOutlined />,
                  onClick: () => navigateToDetails(record?.id),
                },
                // ...(record.status === 'pending' ? [
                //   {
                //     label: "Confirm Booking",
                //     key: "2",
                //     icon: <CheckOutlined />,
                //     onClick: () => showConfirmModal(record),
                //   },
                //   {
                //     label: "Reject Booking",
                //     key: "3",
                //     icon: <CloseOutlined />,
                //     onClick: () => showRejectModal(record),
                //   },
                // ] : []),
              ],
            }}
            trigger={["click"]}
          >
            <Space>
              <MoreOutlined />
            </Space>
          </Dropdown>
        </div>
      ),
    },
  ];

  const getBookingsListAPICCall = async (filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', filters.page);
      params.append('limit', filters.limit);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await axiosInstance.get(
        `${BASE_API_CONFIG.baseURL}/admin/freight-bookings?${params.toString()}`
      );
      console.log("Bookings API Response:", response?.data?.data);
      setBookingsData(response?.data?.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      message.error('Failed to load bookings');
    } finally {
      setLoading(false);
      dispatch(fetchSuccess());
    }
  };

  useEffect(() => {
    dispatch(fetchStart());
    getBookingsListAPICCall(filters);
  }, [dispatch, filters]);

  const onShowSizeChange = (current, pageSize) => {
    setFilters(prev => ({ ...prev, page: current, limit: pageSize }));
  };

  const onSearch = (e) => {
    const value = e?.target?.value || '';
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleSelectStatus = (value) => {
    setFilters(prev => ({ ...prev, status: value, page: 1 }));
  };

 const navigateToDetails = (id) => {
  console.log("Navigating to details with ID:", id);
  console.log("Current history:", history);
  if (id) {
    dispatch(fetchStart());
    const path = `/freight-bookings/details/${id}`;
    console.log("Navigating to path:", path);
    history.push(path);
  } else {
    console.error("No booking ID provided for navigation");
    message.error('Cannot view details: Booking ID missing');
  }
};

  const showConfirmModal = (booking) => {
    setSelectedBooking(booking);
    setConfirmModalVisible(true);
    form.resetFields();
  };

  const showRejectModal = (booking) => {
    setSelectedBooking(booking);
    setRejectModalVisible(true);
  };

  const handleConfirmBooking = async (values) => {
    try {
      await axiosInstance.post(
        `${BASE_API_CONFIG.baseURL}/admin/freight-bookings/${selectedBooking.id}/status`,
        {
          status: 'confirmed',
          shippingCost: values.shippingCost,
          insuranceCost: values.insuranceCost,
          totalCost: values.totalCost,
          adminRemarks: values.adminRemarks,
        }
      );
      setConfirmModalVisible(false);
      form.resetFields();
      message.success('Booking confirmed successfully');
      getBookingsListAPICCall(filters);
    } catch (error) {
      console.error('Error confirming booking:', error);
      message.error('Failed to confirm booking');
    }
  };

  const handleRejectBooking = async (values) => {
    try {
      await axiosInstance.post(
        `${BASE_API_CONFIG.baseURL}/admin/freight-bookings/${selectedBooking.id}/status`,
        {
          status: 'cancelled',
          adminRemarks: values.adminRemarks,
        }
      );
      setRejectModalVisible(false);
      message.success('Booking rejected successfully');
      getBookingsListAPICCall(filters);
    } catch (error) {
      console.error('Error rejecting booking:', error);
      message.error('Failed to reject booking');
    }
  };

  const calculateTotalCost = (shippingCost, insuranceCost) => {
    return (Number(shippingCost) + Number(insuranceCost)).toFixed(2);
  };

  const handleCostChange = () => {
    const shippingCost = form.getFieldValue('shippingCost') || 0;
    const insuranceCost = form.getFieldValue('insuranceCost') || 0;
    form.setFieldValue('totalCost', calculateTotalCost(shippingCost, insuranceCost));
  };

  return (
    <Card
      title={
        <div style={{ display: "flex" }}>
          <div className="myexim-table-title">Freight Bookings</div>
        </div>
      }
    >
      <div className="myexim-table-header" style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
        <div className="search-box" style={{ flex: 1 }}>
          <Input
            placeholder="Search by booking ID or company"
            size="large"
            onChange={onSearch}
            suffix={<SearchOutlined />}
            onPressEnter={onSearch}
          />
        </div>
        
        <Select
          placeholder="Filter by Status"
          style={{ width: 200 }}
          onChange={handleSelectStatus}
          allowClear
          value={filters.status || undefined}
        >
          <Option value="pending">Pending</Option>
          <Option value="confirmed">Confirmed</Option>
          <Option value="in_transit">In Transit</Option>
          <Option value="delivered">Delivered</Option>
          <Option value="cancelled">Cancelled</Option>
        </Select>
      </div>

      <div>
        <Table
          loading={loading}
          rowKey="id"
          rowClassName={(record, index) =>
            index % 2 === 0 ? "table-row-light" : "table-row-dark"
          }
          onRow={(record) => {
            return {
              onClick: (e) => {
                if (!e.target.classList.contains("ant-dropdown-menu-title-content")) {
                  navigateToDetails(record?.id);
                }
              },
            };
          }}
          className="gx-table-responsive my-product-table myexim-table"
          total={bookingsData?.count}
          columns={columns}
          dataSource={bookingsData?.rows || []}
          pagination={{
            position: ["bottomRight"],
            showSizeChanger: true,
            pageSize: filters.limit,
            current: filters.page,
            total: bookingsData?.count || 0,
            onChange: onShowSizeChange,
            onShowSizeChange: onShowSizeChange,
          }}
        />
      </div>

      {/* Confirm Booking Modal */}
      <Modal
        title="Confirm Freight Booking"
        visible={confirmModalVisible}
        onCancel={() => setConfirmModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleConfirmBooking}>
          <Form.Item
            label="Shipping Cost ($)"
            name="shippingCost"
            rules={[{ required: true, message: 'Please enter shipping cost' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={2}
              onChange={handleCostChange}
            />
          </Form.Item>
          
          <Form.Item
            label="Insurance Cost ($)"
            name="insuranceCost"
            rules={[{ required: true, message: 'Please enter insurance cost' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={2}
              onChange={handleCostChange}
            />
          </Form.Item>
          
          <Form.Item
            label="Total Cost ($)"
            name="totalCost"
            rules={[{ required: true, message: 'Please enter total cost' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={2}
              readOnly
            />
          </Form.Item>
          
          <Form.Item
            label="Remarks"
            name="adminRemarks"
          >
            <TextArea rows={4} placeholder="Enter any remarks for the customer" />
          </Form.Item>
          
          <div style={{ textAlign: 'right' }}>
            <Button onClick={() => setConfirmModalVisible(false)} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Confirm Booking
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Reject Booking Modal */}
      <Modal
        title="Reject Freight Booking"
        visible={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form layout="vertical" onFinish={handleRejectBooking}>
          <Form.Item
            label="Reason for Rejection"
            name="adminRemarks"
            rules={[{ required: true, message: 'Please enter reason for rejection' }]}
          >
            <TextArea rows={4} placeholder="Please provide reason for rejecting this booking" />
          </Form.Item>
          
          <div style={{ textAlign: 'right' }}>
            <Button onClick={() => setRejectModalVisible(false)} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" danger htmlType="submit">
              Reject Booking
            </Button>
          </div>
        </Form>
      </Modal>
    </Card>
  );
};

export default FreightBookingsList;